export const MusicXmlImportSteps = ['PENDING', 'STARTED', 'FINGERING_FILLING', 'MUSICXML_PARSING', 'HANDPRINT_PARSING', 'RHYTHMIC_PARSING', 'TIMELINE_GENERATION', 'COMPLETED', 'FAILED'] as const;
export type MusicXmlImportStep = typeof MusicXmlImportSteps[number];
export const MAX_STEP = MusicXmlImportSteps.length - 2;
export const MusicXmlImportProgression: Record<MusicXmlImportStep, number> = {
  PENDING: 0,
  STARTED: 1,
  FINGERING_FILLING: 2,
  MUSICXML_PARSING: 3,
  HANDPRINT_PARSING: 4,
  RHYTHMIC_PARSING: 5,
  TIMELINE_GENERATION: 6,
  COMPLETED: 7,
  FAILED: 7,
};

export const MusicXmlImportStepLabels: Record<MusicXmlImportStep, string> = {
  PENDING: "En attente",
  STARTED: "Démarré",
  FINGERING_FILLING: "Remplissage des doigtés",
  MUSICXML_PARSING: "Analyse du MusicXML",
  HANDPRINT_PARSING: "Analyse de l'empreinte",
  RHYTHMIC_PARSING: "Analyse rythmique",
  TIMELINE_GENERATION: "Génération de la timeline",
  COMPLETED: "Terminé",
  FAILED: "Échoué",
}
export const MusicXmlImportStepDescriptions: Record<MusicXmlImportStep, string> = {
  PENDING: "Votre import est en attente de traitement.",
  STARTED: "Votre import a démarré.",
  FINGERING_FILLING: "Nous remplissons les doigtés manquants dans votre partition.",
  MUSICXML_PARSING: "Nous analysons le fichier MusicXML pour extraire les informations musicales.",
  HANDPRINT_PARSING: "Nous analysons l'empreinte pour associer les notes aux positions de la main.",
  RHYTHMIC_PARSING: "Nous analysons le rythme de la partition.",
  TIMELINE_GENERATION: "Nous générons la timeline pour la lecture du morceau. Cette étape peut prendre du temps",
  COMPLETED: "Votre import est terminé avec succès !",
  FAILED: "Une erreur est survenue lors de l'import de votre partition.",
}

export interface MusicXmlImport {
  id: number;
  step: MusicXmlImportStep;
  failed_reason?: string | ErrorImportCodes;
  started_at?: string;
  completed_at?: string;
  published_version?: {
    id: number;
    piece: {
      url_artist: string;
      url_title: string
    }
  };
  version_id: number;
  readed_at?: string;
}

export const ErrorImportCodesArray = ['UNHANDLED_EXCEPTION', 'ISSUE_IN_MUSICXML_FILE', 'MULTI_VOICE_NOT_SUPPORTED', 'TUPLETS_NOT_SUPPORTED', 'MISSING_FINGERING', 'FILE_NOT_FOUND', 'NO_TIME_SIGNATURE', 'NOTE_NOT_IN_HANDPRINT', 'NO_HANDPRINT_FOUND', 'PIECE_ALREADY_EXISTS', 'INVALID_FILE_TYPE', 'NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT', 'CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS', 'CHORD_SPAN_VERY_SMALL', 'PINEAPPLE_API_ERROR'] as const;
export type ErrorImportCodes = typeof ErrorImportCodesArray[number];

const ErrorMessages: Record<ErrorImportCodes, string> = {
  UNHANDLED_EXCEPTION: "Une exception non gérée s'est produite.",
  ISSUE_IN_MUSICXML_FILE: "Le fichier MusicXML contient des erreurs.",
  MULTI_VOICE_NOT_SUPPORTED: "Les partitions à voix multiples ne sont pas supportées.",
  TUPLETS_NOT_SUPPORTED: "Les notes en triolets ou autres rythmes complexes ne sont pas supportés.",
  MISSING_FINGERING: "Certaines notes n'ont pas de doigté.",
  FILE_NOT_FOUND: "Le fichier MusicXML est introuvable.",
  NO_TIME_SIGNATURE: "La partition doit contenir une indication de mesure (ex : 4/4, 3/4, etc.).",
  NO_HANDPRINT_FOUND: "Aucune empreinte généré dans la partition.",
  NOTE_NOT_IN_HANDPRINT: "Certaines notes ne sont pas dans l'empreinte.",
  PIECE_ALREADY_EXISTS: "Cette partition existe déjà dans votre bibliothèque.",
  NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT: "L'empreinte existante n'a ni position mélodique ni accords associés.",
  CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS: "L'empreinte existante est basée sur des accords, mais aucun accord n'a été trouvé dans la base.",
  CHORD_SPAN_VERY_SMALL: "L'empans d'un accord dans le fichier MusicXML est trop petit pour être valide.",
  INVALID_FILE_TYPE: "Le fichier doit être au format MusicXML (.musicxml, .xml, .mxml).",
  PINEAPPLE_API_ERROR: "Une erreur est survenue lors de la communication avec le service de génération des doigtés.",
}

interface ErrorMessagesContent extends Record<ErrorImportCodes, Object | null> {
  UNHANDLED_EXCEPTION: null,
  ISSUE_IN_MUSICXML_FILE: null,
  MULTI_VOICE_NOT_SUPPORTED: null,
  TUPLETS_NOT_SUPPORTED: null,
  MISSING_FINGERING: {
    hand: string,
    note_id: string,
    measure: number,
    start_division: number,
  },
  FILE_NOT_FOUND: null,
  NO_TIME_SIGNATURE: null,
  NO_HANDPRINT_FOUND: {
    hand: string,
    measure: number,
    division: number,
  },
  NOTE_NOT_IN_HANDPRINT: {
    hand: string,
    measure: number,
    division: number,
  },
  NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT: {
    handprint_id: number,
  }
  CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS: {
    handprint_id: number,
  }
  CHORD_SPAN_VERY_SMALL: {
    new_handprint_code: string,
    combine_fingering: number,
    measure: number,
  }
  INVALID_FILE_TYPE: null,
  PIECE_ALREADY_EXISTS: null,
}

export const getErrorMessage = (errorData: { status: ErrorImportCodes, content?: ErrorMessagesContent[ErrorImportCodes] }): string => {
  const defaultMessage = ErrorMessages[errorData.status];

  if (errorData.content) {
    switch (errorData.status) {
      case 'NO_HANDPRINT_FOUND':
        return `${defaultMessage} (Main: ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).hand}, Mesure: ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).measure}, Division : ${(errorData.content as ErrorMessagesContent['NO_HANDPRINT_FOUND']).division})`;
      case 'NOTE_NOT_IN_HANDPRINT':
        return `${defaultMessage} (Main: ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).hand}, Mesure: ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).measure}, Division : ${(errorData.content as ErrorMessagesContent['NOTE_NOT_IN_HANDPRINT']).division})`;
      case 'MISSING_FINGERING':
        return `${defaultMessage} (Main: ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).hand}, Mesure: ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).measure}, Division : ${(errorData.content as ErrorMessagesContent['MISSING_FINGERING']).start_division})`;
      case 'NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT':
        return `${defaultMessage} (ID de l'empreinte: ${(errorData.content as ErrorMessagesContent['NO_MELODIC_POSITION_AND_NO_CHORDS_ON_EXISTING_HANDPRINT']).handprint_id})`;
      case 'CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS':
        return `${defaultMessage} (ID de l'empreinte: ${(errorData.content as ErrorMessagesContent['CHORD_BASED_EXISTING_HANDPRINT_WITHOUT_CHORDS']).handprint_id})`;
      case 'CHORD_SPAN_VERY_SMALL':
        return `${defaultMessage} (Code de la nouvelle empreinte: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).new_handprint_code}, Combinaison de doigté: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).combine_fingering}, Mesure: ${(errorData.content as ErrorMessagesContent['CHORD_SPAN_VERY_SMALL']).measure})`;
      default:
        return defaultMessage;
    }
  }
  return defaultMessage;
};


